/**
 * DefaultTtlSetting — user setting to choose the default
 * auto-destruct TTL applied to NEW conversations.
 *
 * Why it lives in user-settings (not chat-header):
 *   The chat-header `ConversationTTLPicker` controls a single conv.
 *   This control sets the **starting point** for every conv this
 *   user creates from now on — high-threat-model users can opt every
 *   future conversation into 1h auto-destruct from the start, without
 *   touching each one manually.
 *
 * Backend route: PUT /api/auth/me/default-ttl  (bounds 5min–30days).
 * Existing conversations are never modified by changing this — same
 * "no retro-rewrite" rule the per-conv TTL picker already enforces.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TTL_PRESETS = [
  { hours: 5 / 60, label: '5m', sub: 'burner' },
  { hours: 1, label: '1h', sub: 'short fuse' },
  { hours: 24, label: '24h', sub: 'default' },
  { hours: 24 * 7, label: '7d', sub: 'long thread' },
  { hours: 24 * 30, label: '30d', sub: 'maximum' },
];

export function DefaultTtlSetting() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(
    Number(user?.default_ttl_hours ?? 24),
  );

  useEffect(() => {
    setCurrent(Number(user?.default_ttl_hours ?? 24));
  }, [user?.default_ttl_hours]);

  const apply = async (hours) => {
    if (busy) return;
    if (Math.abs(hours - current) < 1e-3) return;
    setBusy(true);
    try {
      const res = await axios.put(`${API}/auth/me/default-ttl`, {
        default_ttl_hours: hours,
      });
      const next = Number(res.data?.default_ttl_hours ?? hours);
      setCurrent(next);
      // Refresh AuthContext so other surfaces (e.g., a future
      // dashboard chip showing your default) re-read fresh state.
      try { await refreshUser?.(); } catch { /* non-fatal */ }
      toast.success(
        `Default auto-destruct → ${TTL_PRESETS.find((p) => Math.abs(p.hours - next) < 1e-3)?.label || `${next}h`}`,
        { duration: 2400 },
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Update failed';
      toast.error(`Could not update default: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="default-ttl-setting">
      <label className="text-sm text-slate-400">Default Auto-Destruct</label>
      <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
        <div className="flex items-center gap-2 text-amber-400">
          <Clock className="w-5 h-5" />
          <span className="font-medium">
            New conversations start at{' '}
            <span data-testid="default-ttl-current">
              {TTL_PRESETS.find((p) => Math.abs(p.hours - current) < 1e-3)?.label ||
                `${current}h`}
            </span>
          </span>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Sets the starting auto-destruct timer for every conversation
          you create from now on. Existing conversations keep their own
          per-conversation timer — changing this never retro-modifies
          past chats.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {TTL_PRESETS.map((p) => {
            const active = Math.abs(p.hours - current) < 1e-3;
            return (
              <Button
                key={p.hours}
                variant={active ? 'default' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={() => apply(p.hours)}
                data-testid={`default-ttl-preset-${p.label}`}
                className={
                  active
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40 hover:bg-emerald-500/30'
                    : ''
                }
              >
                <span className="font-medium">{p.label}</span>
                <span className="ml-1.5 text-[10px] text-slate-500">
                  {p.sub}
                </span>
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
